import { railToneCss, themeToVars, type Theme } from './tokens';

const RAIL_STYLE_ID = 'sundae-theme-rail';

/**
 * Apply a theme to the document.
 *
 * Two channels, because the token set has two scopes:
 *   1. Root tokens go on <html> as inline custom properties. Inline styles sit
 *      above every stylesheet rule, so they beat the :root defaults in
 *      index.css without needing !important or a class swap.
 *   2. The `.surface-dark` tone overrides go into a single <style> element.
 *      `.surface-dark` is a Tailwind @utility applied to elements deep in the
 *      tree, so it cannot be reached by setting variables on <html>. The
 *      injected rule is UNLAYERED, which in the cascade-layer ordering beats
 *      Tailwind's layered utilities regardless of source order.
 *
 * Safe to call before React mounts and on every subsequent change.
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;

  for (const [cssVar, value] of Object.entries(themeToVars(theme))) {
    root.style.setProperty(cssVar, value);
  }

  // `mode` drives anything that keys off light/dark at the CSS level rather
  // than off a token — currently only native form controls via color-scheme,
  // which is what stops a dark theme rendering white scrollbars and white
  // date pickers.
  root.style.setProperty('color-scheme', theme.mode);
  root.dataset.theme = theme.id;
  root.dataset.themeMode = theme.mode;

  let styleEl = document.getElementById(RAIL_STYLE_ID) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = RAIL_STYLE_ID;
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = railToneCss(theme);

  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) {
    meta.content = theme.chromeColor;
  }
}
